import React, { useEffect, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CardMedia, IconButton, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { J1DownarrowD } from '@e3dc-react/icons'
import { LineItem } from 'src/Libs/shop/models'
import { useDispatch } from 'react-redux'
import { setQuantity as setConfiguratorQuantity } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { currencyFormatter } from '../../utils/formatter'
import ClearIcon from '@mui/icons-material/Clear'
import { useDebounce } from '@e3dc-react/shell/Hooks/useDebounce'
import { LoadingButton } from '@mui/lab'
import { PRODUCT_CART_DEBOUNCE_TIMER } from '../../consts'
import { ShopImageThumnbailSizes, ShopSystemConfigurationTypes, ProductGeneralType } from '../../types'
import { getImageUrlBySize } from '../../utils'
import ButtonQuantityGroup from '../ShopProduct/ButtonQuantityGroup'

interface SystemConfigurationCheckoutOverviewProps {
  configurator: LineItem
  isLoading: boolean
  onUpdateSystemConfigurationCart: (productId: string, quantity: number) => void
  onDeleteSystemConfigurationCart: (productId: string, quantity: number) => void
  onEditSystemConfigurator: (productId: string, configuratorType: ProductGeneralType) => void
}

const useStyles = makeStyles()(theme => ({
  productWrapper: {
    display: 'block',
    minWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      paddingBottom: theme.spacing(8),
      borderBottom: `1px solid ${theme.palette.shades.shade03}`,
    },
  },
  subProduct: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginLeft: theme.spacing(12),
    paddingTop: theme.spacing(2),
  },
  product: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'flex-start',
    },
  },
  productImage: {
    minWidth: '84px',
    maxWidth: '84px',
    height: 'auto',
    objectFit: 'contain',
  },
  productContent: {
    paddingLeft: theme.spacing(1),
    display: 'block',
    flex: 'auto',
  },
  productTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    minWidth: '100%',
  },
  acordion: {
    boxShadow: 'none',
    margin: 0,
    padding: 0,
    minWidth: '100%',
  },
  expandedPanel: {
    margin: 0,
    padding: 0,
  },
  closeIconButton: {
    border: `1px solid ${theme.palette.shades.shade02}`,
    backgroundColor: theme.palette.shades.shade04,
    borderRadius: theme.shape.borderRadius,
    minWidth: '36px',
    minHeight: '36px',
    marginLeft: theme.spacing(1),
    padding: 0,
    color: theme.palette.buttonGroup.inactiveColor,
  },
  sideContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  quantityContainer: {
    display: 'flex',
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
    },
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(2),
    transform: 'translateX(-12px)',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'flex-start',
      flexDirection: 'row-reverse',
    },
  },
  accordionButton: {
    width: '36px',
    height: '36px',
    transform: 'translateX(12px)',
  },

  accordionIcon: {
    minWidth: '36px',
    minHeight: '36px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: theme.shape.borderRadius,
  },
  normalPriceContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  accordionRoot: {
    padding: 0,
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'inherit',
  },
  accordionContent: {
    alignItems: 'flex-start',
  },
  fixFlexTitle: {
    paddingRight: theme.spacing(2),
    marginTop: 0,
  },
}))

/*
 * This component shows System Configured Product to the Shop Cart Sidebar.
 * It shows also Children products that are part of the System Configurator.
 * this also handle the Quantity change or Delete of product.
 */

const SystemConfigurationCheckoutOverview: React.FC<SystemConfigurationCheckoutOverviewProps> = ({
  configurator,
  isLoading,
  onUpdateSystemConfigurationCart,
  onDeleteSystemConfigurationCart,
  onEditSystemConfigurator,
}) => {
  const [expand, setExpand] = useState<boolean>(false)
  const dispatch = useDispatch()
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const [quantity, setQuantity] = useState<number>(configurator.quantity)
  const debouncedQuantity = useDebounce(quantity, PRODUCT_CART_DEBOUNCE_TIMER)

  useEffect(() => {
    if (debouncedQuantity !== configurator.quantity) {
      dispatch(setConfiguratorQuantity(debouncedQuantity))
      onUpdateSystemConfigurationCart(configurator.id, debouncedQuantity)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuantity])

  const totalPrice = configurator.price.totalPrice
  const unitPrice = (configurator.price.listPrice && configurator.price.listPrice?.price * configurator.price.quantity) ?? 0
  const configuratorQuantityLimit = configurator.quantityInformation.maxPurchase
  const configuratorQuantityType = configurator.payload.quantityType
  const configuratorId = configurator.id
  const hasDiscount = unitPrice > totalPrice
  const minQuantity = configurator.quantityInformation.minPurchase
  const maxQuantity = configurator.quantityInformation.maxPurchase
  const configurationType = configurator.type
  const productImageUrl = getImageUrlBySize(configurator?.cover?.thumbnails, ShopImageThumnbailSizes.Small)
  const fixOrFlexTitle =
    configuratorQuantityType === ShopSystemConfigurationTypes.Fix
      ? `${configuratorQuantityLimit} ${configuratorQuantityType} - ${t('shop.system-configure-checkout.quantities-available')}`
      : ` ${configuratorQuantityType} - ${t('shop.system-configure-checkout.quantities-available')}`
  return (
    <Box className={classes.productWrapper}>
      <Box className={classes.product}>
        <Accordion expanded={expand} sx={{ position: 'inherit' }} classes={{ root: classes.acordion, expanded: classes.expandedPanel }}>
          <AccordionSummary
            className={classes.accordionRoot}
            classes={{
              content: classes.accordionContent,
            }}
          >
            <CardMedia className={classes.productImage} component="img" image={productImageUrl} />
            <Box className={classes.productContent}>
              <Box className={classes.productTitle}>
                <Box sx={{ display: 'block' }}>
                  <Typography sx={{ paddingRight: '12px' }}>
                    {configurator.label !== '' ? configurator.label : t('shop.system-configure-checkout.system-configure-label')}
                  </Typography>
                </Box>
                <Box className={classes.sideContainer}>
                  <Box className={classes.quantityContainer}>
                    <ButtonQuantityGroup
                      quantity={quantity}
                      setQuantity={quantity => {
                        setQuantity(quantity)
                      }}
                      minQuantity={minQuantity}
                      maxQuantity={maxQuantity}
                      isLoading={isLoading}
                    />
                    <LoadingButton
                      disabled={isLoading}
                      onClick={() => {
                        onDeleteSystemConfigurationCart(configurator.id, 0)
                      }}
                      className={classes.closeIconButton}
                      size="small"
                    >
                      <ClearIcon />
                    </LoadingButton>
                  </Box>
                </Box>
              </Box>
              <Typography variant="body2" className={classes.fixFlexTitle}>
                {fixOrFlexTitle}
              </Typography>
              <Box className={classes.subContainer}>
                <Box className={classes.buttonsContainer}>
                  <IconButton className={classes.accordionButton} onClick={() => setExpand(!expand)}>
                    <J1DownarrowD
                      className={classes.accordionIcon}
                      color="inherit"
                      sx={{
                        transform: expand ? 'translate(0, 0) rotateX(180deg)' : 'none',
                      }}
                    />
                  </IconButton>
                  <Button
                    variant="text"
                    onClick={() => {
                      onEditSystemConfigurator(configuratorId, configurationType)
                    }}
                  >
                    {t('shop.system-configure-checkout.edit-button-text')}
                  </Button>
                </Box>
                <Box className={classes.priceContainer}>
                  <Box className={classes.normalPriceContainer}>
                    {hasDiscount && (
                      <Typography variant="subtitle2" align="right" color="inherit" sx={{ marginRight: 2, textDecoration: 'line-through' }}>
                        {currencyFormatter(unitPrice)}
                      </Typography>
                    )}
                    <Typography variant="h6" align="right" color="primary">
                      {currencyFormatter(totalPrice)}
                    </Typography>
                  </Box>
                  {hasDiscount && (
                    <Typography variant="h4" align="right" color="primary">
                      {`${t('shop.system-configure-checkout.saved-price')}`}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ paddingRight: 0 }}>
            {configurator.children.map(subProducts => {
              const totalProductPrice = currencyFormatter(subProducts.price.totalPrice)
              const subProductImageUrl = getImageUrlBySize(subProducts.cover?.thumbnails, ShopImageThumnbailSizes.Medium)
              return (
                <Box className={classes.subProduct} key={subProducts.id}>
                  <CardMedia className={classes.productImage} component="img" image={subProductImageUrl} />
                  <Box className={classes.productContent}>
                    <Box className={classes.productTitle}>
                      <Typography sx={{ paddingRight: '12px' }}>{subProducts.label}</Typography>
                    </Box>
                    <Typography variant="h6" align="right" color="primary">
                      {totalProductPrice}
                    </Typography>
                  </Box>
                </Box>
              )
            })}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  )
}

export default SystemConfigurationCheckoutOverview
